import { useMutation } from '@apollo/client';
import { UPDATE_AI_FEATURE } from '@nile/banyan/src/Graphql/Mutations/ai';
import { GET_AI_FEATURES } from '@nile/banyan/src/Graphql/Query/ai';
import '@nile/components/src/Utils/XhrErrorHandler'; // Xhr request error handling
import { AccountClient } from '@nile/graphql';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { EventEmitter } from '@nile/components';

let aiFeatureData = null;

const AIFeaturesHook = () => {
  const [aiFeatures, setAIFeatures] = useState(aiFeatureData);

  const [updateAIFeatureMutation] = useMutation(UPDATE_AI_FEATURE, {
    client: AccountClient,
    onCompleted: () => {
      // Refresh the AI features data after successful update
      getAIFeaturesData();
    },
    onError: (error) => {
      console.error('Error updating AI feature:', error);
    }
  });

  useEffect(() => {
    aiFeatureData = aiFeatures;
  }, [aiFeatures]);

  const getAIFeaturesData = async () => {
    const res = await AccountClient.query({
      query: GET_AI_FEATURES,
      variables: {
        input: {
          listInfo: {
            pageSize: 50,
            page: 1
          }
        }
      },
      fetchPolicy: 'network-only'
    });
    const features = res?.data?.getAIFeatureList?.feature;
    if (!isEmpty(features)) {
      const featureData = features.map((feat) => {
        const inProgress =
          feat?.state === 'IN_PROGRESS' ||
          feat?.state === 'ENABLED_IN_PROGRESS' ||
          feat?.state === 'DISABLED_IN_PROGRESS';
        const isActive = feat?.state === 'ACTIVE';
        const enabled = isActive && feat?.enabledForLoggedInUser;
        const disabled = feat?.state === 'INACTIVE';
        return {
          featureId: feat?.featureId,
          featureConstant: feat?.constant,
          constant: feat?.constant,
          enabled,
          disabled,
          inProgress,
          isActive,
          state: feat?.state,
          subFeatures: feat?.subFeatures,
          name: feat?.name,
          description: feat?.description,
          enabledForLoggedInUser: feat?.enabledForLoggedInUser,
          displayIconUrl: feat?.displayIconUrl
        };
      });
      aiFeatureData = featureData;
      setAIFeatures(featureData);
      const promptBuilder = featureData.find(
        (feat) => feat?.constant === 'PROMPT_BUILDER'
      );
      EventEmitter.dispatch('aiFeaturesUpdated', {
        promptBuilderSubFeatures: promptBuilder?.subFeatures || [],
        features: featureData
      });
      return featureData;
    }
  };

  const getIsAIFeatureEnabled = (constant) => {
    return aiFeatures?.some((feature) => {
      // Match parent
      if (feature.constant === constant) {
        return feature?.enabled;
      }

      // Match subFeature
      return (
        feature?.enabled &&
        (feature?.subFeatures?.some(
          (subFeature) =>
            subFeature?.constant === constant && subFeature?.enabled
        ) ??
          false)
      );
    });
  };

  const getAIFeatureData = (name) => {
    // Check top-level feature
    const feature = aiFeatureData.find((f) => f.constant === name);
    if (feature) {
      return { ...feature, isSubFeature: false };
    }

    // Check subFeatures
    const subFeatureParent = aiFeatureData.find((f) =>
      f.subFeatures?.some((sub) => sub.constant === name)
    );

    if (subFeatureParent) {
      const subFeature = subFeatureParent.subFeatures.find(
        (sub) => sub.constant === name
      );

      return {
        ...subFeature,
        isSubFeature: true,
        parentFeature: { ...subFeatureParent } // include full parent feature
      };
    }

    return null;
  };

  const updateAIFeature = async (variables) => {
    try {
      const result = await updateAIFeatureMutation({
        variables: variables
      });
      return result;
    } catch (error) {
      console.error('Error updating AI feature:', error);
      throw error;
    }
  };

  return {
    aiFeatures: aiFeatures,
    getAIFeaturesData: getAIFeaturesData,
    getIsAIFeatureEnabled: getIsAIFeatureEnabled,
    getAIFeatureData: getAIFeatureData,
    updateAIFeature: updateAIFeature
  };
};

export default AIFeaturesHook;
